import {
    Button,
    HStack,
    ModalBody,
    Text,
    VStack,
} from '@chakra-ui/react';
import { FaIcon } from '@acp/core-ui';
import {
    faChevronRight, faLockKeyhole, faLockKeyholeOpen,
} from '@fortawesome/pro-light-svg-icons';
import { AccessIssueEntryMethodLabel } from '@acp/shared';
import { useMemo } from 'react';
import { AccessIssuesWizardMachineContext } from '../machine/context';
import { useEntryMethodDetails } from '../hooks';
import { AccessIssuesWizardHeader } from '../components/wizard-header';

function EntryMethodQueueButton({
    entryMethod,
    isBlocked = false,
}: {
    entryMethod: AccessIssueEntryMethodLabel;
    isBlocked?: boolean;
}) {
    const machine = AccessIssuesWizardMachineContext.useActorRef();
    const blockedEntryMethod = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.blockedEntryMethodQueues[entryMethod]!
    );
    const definition = useEntryMethodDetails(entryMethod);

    return (
        <Button
            onClick={() => machine.send({ type: 'SELECT_ENTRY_METHOD', entryMethod })}
            variant="unstyled"
            borderWidth={1}
            borderColor="muted.100"
            alignItems="start"
            textAlign="start"
            h="auto"
            py={10}
            px={10}
            _hover={{
                bgColor: 'muted.25',
            }}
            isDisabled={!isBlocked}
            whiteSpace="normal"
        >
            <HStack gap={8}>
                <FaIcon w={7} h={7} icon={isBlocked ? faLockKeyhole : faLockKeyholeOpen} color={isBlocked ? 'danger.500' : 'success.500'} />
                <VStack alignItems="start" gap={0} w="100%">
                    <Text fontWeight="semibold" fontSize="lg">
                        Arrange access via
                        {' '}
                        {definition.name.toLocaleLowerCase()}
                    </Text>
                    {
                        isBlocked
                        && (
                            <Text fontSize="sm" fontWeight="normal" color="muted.500">
                                {definition.description}
                            </Text>
                        )
                    }
                    { !isBlocked && (
                        <Text fontSize="sm" fontWeight="normal" color="success.500">
                            You&apos;ve already unblocked this entry method
                        </Text>
                    ) }
                    {
                        isBlocked
                        && blockedEntryMethod.is_blocked_by_preferences
                        && (
                            <Text fontSize="sm" fontWeight="normal" color="danger.500">
                                This entry method is prevented by your preferences
                            </Text>
                        )
                    }
                </VStack>
                {isBlocked && <FaIcon w={4} h={4} icon={faChevronRight} color="muted.300" />}
            </HStack>
        </Button>
    );
}

function useSortedEntryMethods({
    labels,
}: {
    labels: AccessIssueEntryMethodLabel[];
}) {
    const sortedLabels = useMemo(() => {
        const labelCopy = [...labels];

        labelCopy.sort((a, b) => a.localeCompare(b));

        return labelCopy;
    }, [labels]);

    return sortedLabels;
}

function AccessIssuesWizardSelectEntryMethodScreen() {
    const availableEntryMethods = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.entryMethods.available
    );

    const sortedAvailable = useSortedEntryMethods({
        labels: availableEntryMethods,
    });

    const resolvedEntryMethods = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.entryMethods.resolved
    );

    const sortedResolved = useSortedEntryMethods({
        labels: resolvedEntryMethods,
    });

    return (
        <ModalBody as={VStack} py={6} alignItems="stretch" gap={6}>
            <AccessIssuesWizardHeader />
            <VStack w="100%" alignItems="stretch" gap={6}>
                <Text fontSize="md" fontWeight="medium">
                    Before you proceed, please let us know
                    the preferred method of entry for this property:
                </Text>
                { sortedAvailable.map((entryMethod) => (
                    <EntryMethodQueueButton
                        key={entryMethod}
                        entryMethod={entryMethod}
                        isBlocked
                    />
                )) }
                { sortedResolved.map((entryMethod) => (
                    <EntryMethodQueueButton
                        key={entryMethod}
                        entryMethod={entryMethod}
                    />
                )) }
            </VStack>
        </ModalBody>
    );
}

export { AccessIssuesWizardSelectEntryMethodScreen };
